import api from './api';
import { AxiosProgressEvent } from 'axios';

export interface FreeKey {
  id?: number;
  stt: number;
  quan_doan?: string;
  mon_phai?: string;
  viral_key?: string;
  rpm?: string;
  key: string;
  views: number;
  ngay_dang?: string;
  period_type: 'year' | 'month' | 'week' | 'day';
  period_value: string;
  is_active: boolean;
  display_order: number;
  prompt?: string;
  kenh_doi_thu?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FreeKeyPublic {
  id: number;
  stt: number;
  quan_doan?: string;
  mon_phai?: string;
  viral_key?: string;
  rpm?: string;
  key: string;
  views: number;
  ngay_dang?: string;
  period_type: string;
  period_value: string;
  display_order: number;
  prompt?: string;
  kenh_doi_thu?: string;
}

export interface FreeKeysPublicResponse {
  keys: FreeKeyPublic[];
  total: number;
  can_view_full: boolean;
  message?: string;
}

export interface PaginatedResponse<T> {
  keys: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export const freeKeyService = {
  // Public - Get keys by period
  getKeysByPeriod: async (
    periodType: 'year' | 'month' | 'week' | 'day',
    periodValue: string
  ): Promise<FreeKeysPublicResponse> => {
    const response = await api.get<FreeKeysPublicResponse>(
      '/free-keys/public/list',
      {
        params: {
          period_type: periodType,
          period_value: periodValue,
        },
      }
    );
    return response.data;
  },

  // Admin - List all keys
  list: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    period_type?: string;
    period_value?: string;
    is_active?: string;
  }): Promise<PaginatedResponse<FreeKey>> => {
    const response = await api.get<PaginatedResponse<FreeKey>>(
      '/free-keys/admin/list',
      { params }
    );
    return response.data;
  },

  // Admin - Get single key
  get: async (id: number): Promise<FreeKey> => {
    const response = await api.get<{ key: FreeKey }>(
      `/free-keys/admin/${id}`
    );
    return response.data.key;
  },

  // Admin - Create key
  create: async (data: Partial<FreeKey>): Promise<FreeKey> => {
    const response = await api.post<{
      message: string;
      key: FreeKey;
    }>('/free-keys/admin/create', data);
    return response.data.key;
  },

  // Admin - Update key
  update: async (
    id: number,
    data: Partial<FreeKey>
  ): Promise<FreeKey> => {
    const response = await api.put<{
      message: string;
      key: FreeKey;
    }>(`/free-keys/admin/${id}`, data);
    return response.data.key;
  },

  // Admin - Delete key
  delete: async (id: number): Promise<void> => {
    await api.delete(`/free-keys/admin/${id}`);
  },

  // Admin - Bulk delete keys
  bulkDelete: async (keyIds: number[]): Promise<{ message: string; deleted_count: number }> => {
    const response = await api.post<{
      message: string;
      deleted_count: number;
    }>('/free-keys/admin/bulk-delete', { key_ids: keyIds });
    return response.data;
  },

  // Admin - Import from Excel
  importExcel: async (
    file: File,
    periodType: 'year' | 'month' | 'week' | 'day',
    periodValue: string,
    onProgress?: (progress: number) => void
  ): Promise<{ message: string; created: number; errors?: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('period_type', periodType);
    formData.append('period_value', periodValue);

    const response = await api.post<{
      message: string;
      created: number;
      errors?: string[];
    }>('/free-keys/admin/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });

    return response.data;
  },

  // Admin - Export to Excel
  exportExcel: async (params?: {
    period_type?: string;
    period_value?: string;
  }): Promise<Blob> => {
    const response = await api.get('/free-keys/admin/export-excel', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

